// Top-nav icon set, taken from the Inkfree Dashboard reference. Same drawing
// rules as the side-nav set in `DrawerIcons`: a 24-box stroke glyph at 1.9
// weight with round caps/joins, coloured by `currentColor` so the surrounding
// button controls the resting and hover shades.
import { SvgIcon, SvgIconProps } from "@mui/material";

// These MUST go through `sx`, not svg presentation attributes: MuiSvgIcon-root
// sets `fill: currentColor` in CSS, and a CSS declaration on the element beats
// a `fill="none"` attribute on that same element — which renders every glyph as
// a solid blob. Merged ahead of the caller's sx so `fontSize` still overrides.
const strokeIconSx = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.9,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const withStroke = (sx: SvgIconProps["sx"]) => [
  strokeIconSx,
  ...(Array.isArray(sx) ? sx : [sx]),
];

/** Inbox — open tray. */
export const InboxNavIcon = ({ sx, ...props }: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props} sx={withStroke(sx)}>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </SvgIcon>
);

/** Notifications — bell. */
export const BellNavIcon = ({ sx, ...props }: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props} sx={withStroke(sx)}>
    <path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </SvgIcon>
);

/** Sign out — arrow leaving a door. */
export const SignOutNavIcon = ({ sx, ...props }: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props} sx={withStroke(sx)}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </SvgIcon>
);

/** Help — question mark in a circle. */
export const HelpNavIcon = ({ sx, ...props }: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props} sx={withStroke(sx)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.2 9a2.8 2.8 0 0 1 5.4 1c0 1.9-2.6 2.3-2.6 3.5" />
    <path d="M12 17h.01" />
  </SvgIcon>
);

/** Help Center — a manual. Tile glyph inside the Help & support panel. */
export const HelpCenterTileIcon = ({ sx, ...props }: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props} sx={withStroke(sx)}>
    <path d="M6.5 3H19a1 1 0 0 1 1 1v13H6.5A2.5 2.5 0 0 0 4 19.5v-14A2.5 2.5 0 0 1 6.5 3z" />
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20v4H6.5A2.5 2.5 0 0 1 4 19.5z" />
  </SvgIcon>
);

/** Support Tickets — a ticket stub. Tile glyph inside the Help & support panel. */
export const TicketTileIcon = ({ sx, ...props }: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props} sx={withStroke(sx)}>
    <path d="M3 9V7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2a3 3 0 0 0 0 6v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a3 3 0 0 0 0-6z" />
    <path d="M13 6.5v2M13 11v2M13 15.5v2" />
  </SvgIcon>
);
